import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Container, Card, CardBody, Spinner, Alert } from 'reactstrap';
import { Helmet } from 'react-helmet-async';
import HideCookieConsent from 'Components/General/HideCookieconsent';
import Header from '../Components/Header/Header';
import logo from 'assets/logo_green_tm.png';
import 'css/staticpage.scss';

const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com';
  return apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
};

const getStoredAuthToken = () => {
  return window.localStorage.getItem('HMI_AUTH_TOKEN') || window.localStorage.getItem('token') || '';
};

const QRCodePage = ({ match, isAuthenticated, currentUser }) => {
  const code = match?.params?.code || '';
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch invitation data on mount
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!code) {
        setIsLoading(false);
        setError('No QR code provided');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const baseUrl = getApiBaseUrl();
        const token = getStoredAuthToken();

        // Record click (fire and forget)
        fetch(`${baseUrl}/api/invitations/record-click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviteCode: code })
        }).catch(() => {});

        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(`${baseUrl}/api/invitations/validate/${code}`, {
          method: 'GET',
          headers
        });

        const json = await res.json();
        if (!isMounted) return;

        if (res.ok && json.success && json.data?.invitation) {
          setInvitationData(json.data.invitation);
        } else {
          setError(json.error?.message || t('Invitation not found or expired'));
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading QR code data:', err);
        setError(err.message || t('Failed to load invitation'));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();
    return () => { isMounted = false; };
  }, [code, t]);

  // Handle deep link to open in app
  const openInApp = useCallback(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

    const appUrl = `heresmyinfo://invite/${code}`;
    const IOS_STORE = 'https://apps.apple.com/us/app/heresmyinfo/id1439939855';
    const ANDROID_STORE = 'https://play.google.com/store/apps/details?id=com.heresmyinfo.client';

    if (isAndroid || isIOS) {
      window.location.href = appUrl;
      setTimeout(() => {
        if (!document.hidden) {
          window.location.href = isAndroid ? ANDROID_STORE : IOS_STORE;
        }
      }, 2500);
    } else {
      // Desktop - copy link and inform user
      navigator.clipboard.writeText(`https://hmiqr.com/qr/${code}`).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [code]);

  // Handle accepting invitation directly from web (for authenticated users)
  const handleAcceptInvitation = async () => {
    const token = getStoredAuthToken();
    if (!token) return;

    try {
      setIsAccepting(true);
      setAcceptError(null);
      const baseUrl = getApiBaseUrl();

      const res = await fetch(`${baseUrl}/api/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invitation: code,
          verificationMethod: 'qr',
          subject: 'Connection Request Accepted via QR'
        })
      });

      const json = await res.json();
      if (res.ok && json.success) {
        setAcceptSuccess(true);
      } else {
        setAcceptError(json.error?.message || t('Failed to accept invitation'));
      }
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      setAcceptError(err.message || t('Failed to accept invitation'));
    } finally {
      setIsAccepting(false);
    }
  };

  const handleCopyLink = () => {
    const url = `https://hmiqr.com/qr/${code}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const fromName = invitationData?.fromName || invitationData?.fromUsername || invitationData?.fromEmail || t('A HeresMyInfo user');
  const cardName = invitationData?.cardName || t('Contact Card');
  const isOwner = Boolean(
    invitationData?.isOwner ||
    (currentUser && invitationData && (currentUser.id === invitationData.fromUserId || currentUser.email === invitationData.fromEmail))
  );
  const isAlreadyConnected = Boolean(invitationData?.alreadyConnected);

  return (
    <>
      <Helmet>
        <title>{fromName ? `${fromName} - HeresMyInfo Invitation` : 'HeresMyInfo Invitation'}</title>
      </Helmet>
      <Header />
      <HideCookieConsent />

      <div style={{
        minHeight: 'calc(100vh - 70px)',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 15px'
      }}>
        <Container style={{ maxWidth: '520px' }}>
          <Card style={{
            borderRadius: '20px',
            border: 'none',
            boxShadow: '0 12px 36px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
            {/* Header Accent Bar */}
            <div style={{
              height: '8px',
              background: 'linear-gradient(90deg, #0F6812 0%, #168019 50%, #34D399 100%)'
            }} />

            <CardBody style={{ padding: '36px 28px', textAlign: 'center' }}>
              <div style={{ marginBottom: '20px' }}>
                <img src={logo} alt="HeresMyInfo" width="220" style={{ maxWidth: '100%', height: 'auto' }} />
              </div>

              {isLoading ? (
                <div style={{ padding: '40px 0' }}>
                  <Spinner style={{ width: '3rem', height: '3rem', color: '#0F6812' }} />
                  <p style={{ marginTop: '16px', color: '#64748b', fontSize: '15px' }}>
                    {t('Loading invitation details...')}
                  </p>
                </div>
              ) : error ? (
                <div style={{ padding: '20px 0' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#fee2e2',
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    margin: '0 auto 16px'
                  }}>
                    ✕
                  </div>
                  <h4 style={{ fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                    {t('Invitation Not Found')}
                  </h4>
                  <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
                    {error}
                  </p>
                  <Button
                    color="primary"
                    tag={Link}
                    to="/"
                    style={{
                      backgroundColor: '#0F6812',
                      borderColor: '#0F6812',
                      borderRadius: '10px',
                      padding: '10px 24px',
                      fontWeight: '600'
                    }}
                  >
                    {t('Go to Home')}
                  </Button>
                </div>
              ) : acceptSuccess ? (
                <div style={{ padding: '20px 0' }}>
                  <div style={{
                    width: '68px',
                    height: '68px',
                    borderRadius: '50%',
                    backgroundColor: '#dcfce7',
                    color: '#16a34a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    margin: '0 auto 16px'
                  }}>
                    ✓
                  </div>
                  <h4 style={{ fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                    {t('Connected Successfully!')}
                  </h4>
                  <p style={{ color: '#475569', fontSize: '15px', marginBottom: '24px' }}>
                    {t('You are now connected with')} <strong>{fromName}</strong>. {t('Your contact information has been exchanged.')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Button
                      color="primary"
                      tag={Link}
                      to="/personal"
                      style={{
                        backgroundColor: '#0F6812',
                        borderColor: '#0F6812',
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}
                    >
                      {t('View My Contacts')}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      onClick={openInApp}
                      style={{
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '15px'
                      }}
                    >
                      {t('Open in Mobile App')}
                    </Button>
                  </div>
                </div>
              ) : isOwner ? (
                /* CASE: User scanned their own QR code */
                <div style={{ padding: '10px 0' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#e0f2fe',
                    color: '#0284c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    margin: '0 auto 16px'
                  }}>
                    🏷️
                  </div>
                  <h4 style={{ fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                    {t('Your Card QR Code')}
                  </h4>
                  <div style={{
                    display: 'inline-block',
                    backgroundColor: '#ecfdf5',
                    color: '#065f46',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontWeight: '600',
                    fontSize: '13px',
                    marginBottom: '16px'
                  }}>
                    {cardName}
                  </div>
                  <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
                    {t('This is the QR code invitation for your card. Anyone who scans it will be prompted to connect with you.')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Button
                      color="primary"
                      onClick={handleCopyLink}
                      style={{
                        backgroundColor: '#0F6812',
                        borderColor: '#0F6812',
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '15px'
                      }}
                    >
                      {copied ? t('✓ Link Copied!') : t('Copy Shareable Link')}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      tag={Link}
                      to="/my-profile"
                      style={{
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '15px'
                      }}
                    >
                      {t('Manage My Cards')}
                    </Button>
                  </div>
                </div>
              ) : isAlreadyConnected ? (
                /* CASE: Users are already connected */
                <div style={{ padding: '10px 0' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#dcfce7',
                    color: '#16a34a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '30px',
                    margin: '0 auto 16px'
                  }}>
                    🤝
                  </div>
                  <h4 style={{ fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                    {t('Already Connected')}
                  </h4>
                  <p style={{ color: '#475569', fontSize: '15px', marginBottom: '24px', lineHeight: 1.5 }}>
                    {t('You are already connected with')} <strong>{fromName}</strong>.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Button
                      color="primary"
                      onClick={openInApp}
                      style={{
                        backgroundColor: '#0F6812',
                        borderColor: '#0F6812',
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}
                    >
                      {t('Open in Mobile App')}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      tag={Link}
                      to="/personal"
                      style={{
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '15px'
                      }}
                    >
                      {t('View Contact in Web')}
                    </Button>
                  </div>
                </div>
              ) : (
                /* CASE: Valid Invitation to Connect */
                <div>
                  {/* Sender Avatar Badge */}
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #0F6812 0%, #168019 100%)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    fontWeight: '700',
                    margin: '0 auto 16px',
                    boxShadow: '0 6px 16px rgba(15, 104, 18, 0.25)'
                  }}>
                    {fromName.charAt(0).toUpperCase()}
                  </div>

                  <h3 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}>
                    {fromName}
                  </h3>

                  <div style={{
                    display: 'inline-block',
                    backgroundColor: '#ecfdf5',
                    color: '#065f46',
                    padding: '4px 14px',
                    borderRadius: '20px',
                    fontWeight: '600',
                    fontSize: '13px',
                    marginBottom: '16px'
                  }}>
                    {t('Sharing')}: {cardName}
                  </div>

                  <p style={{ color: '#64748b', fontSize: '15px', lineHeight: 1.5, marginBottom: '24px' }}>
                    {t('Connect on HeresMyInfo to view full contact details and share your information back.')}
                  </p>

                  {acceptError && (
                    <Alert color="danger" style={{ borderRadius: '10px', fontSize: '14px', marginBottom: '16px' }}>
                      {acceptError}
                    </Alert>
                  )}

                  {/* Actions depending on auth state */}
                  {isAuthenticated ? (
                    /* Authenticated user: can Accept directly */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Button
                        color="primary"
                        size="lg"
                        disabled={isAccepting}
                        onClick={handleAcceptInvitation}
                        style={{
                          backgroundColor: '#0F6812',
                          borderColor: '#0F6812',
                          borderRadius: '12px',
                          padding: '13px',
                          fontWeight: '600',
                          fontSize: '16px',
                          boxShadow: '0 4px 12px rgba(15, 104, 18, 0.3)'
                        }}
                      >
                        {isAccepting ? (
                          <>
                            <Spinner size="sm" style={{ marginRight: '8px' }} />
                            {t('Connecting...')}
                          </>
                        ) : (
                          t('Accept & Connect')
                        )}
                      </Button>

                      <Button
                        outline
                        color="secondary"
                        size="lg"
                        onClick={openInApp}
                        style={{
                          borderRadius: '12px',
                          padding: '12px',
                          fontWeight: '600',
                          fontSize: '15px'
                        }}
                      >
                        {t('Open in Mobile App')}
                      </Button>
                    </div>
                  ) : (
                    /* Unauthenticated recipient: Mobile deep link, sign up, or sign in */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Button
                        color="primary"
                        size="lg"
                        onClick={openInApp}
                        style={{
                          backgroundColor: '#0F6812',
                          borderColor: '#0F6812',
                          borderRadius: '12px',
                          padding: '13px',
                          fontWeight: '600',
                          fontSize: '16px',
                          boxShadow: '0 4px 12px rgba(15, 104, 18, 0.3)'
                        }}
                      >
                        {t('Open in Mobile App')}
                      </Button>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                          color="success"
                          outline
                          tag={Link}
                          to={{
                            pathname: '/signup',
                            state: { from: { pathname: `/qr/${code}` } }
                          }}
                          style={{
                            flex: 1,
                            borderRadius: '12px',
                            padding: '12px',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderColor: '#0F6812',
                            color: '#0F6812'
                          }}
                        >
                          {t('Sign Up')}
                        </Button>
                        <Button
                          color="secondary"
                          outline
                          tag={Link}
                          to={{
                            pathname: '/login',
                            state: { from: { pathname: `/qr/${code}` } }
                          }}
                          style={{
                            flex: 1,
                            borderRadius: '12px',
                            padding: '12px',
                            fontWeight: '600',
                            fontSize: '15px'
                          }}
                        >
                          {t('Sign In')}
                        </Button>
                      </div>

                      {/* Store Links */}
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #f1f5f9'
                      }}>
                        <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>
                          {t("Don't have the app? Download it for free:")}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <a
                            href="https://apps.apple.com/us/app/heresmyinfo/id1439939855"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: '#000000',
                              color: '#ffffff',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              textDecoration: 'none',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            🍎 App Store
                          </a>
                          <a
                            href="https://play.google.com/store/apps/details?id=com.heresmyinfo.client"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: '#000000',
                              color: '#ffffff',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              textDecoration: 'none',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            ▶ Google Play
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Share Link Helper */}
                  <div style={{ marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#64748b',
                        fontSize: '13px',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      {copied ? t('✓ Code Copied to Clipboard!') : `${t('Invitation Code')}: ${code}`}
                    </button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: Boolean(state.auth?.isAuthenticated),
  currentUser: state.auth?.currentUser
});

export default connect(mapStateToProps)(QRCodePage);